import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Zap, Calculator, Plane, Leaf } from 'lucide-react';

interface CalculatorResultsProps {
  results: {
    totalEnergy_kWh: number;
    totalCO2_kg: number;
    flightsParisToNY: number;
  };
}

const CarbonCalculatorResults: React.FC<CalculatorResultsProps> = ({ results }) => {
  const [compensationPeriod, setCompensationPeriod] = useState<5 | 10 | 20>(5);

  const treesToOffset = results.totalCO2_kg / (compensationPeriod * 21); // 21 kg CO2 per tree per year

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-xl shadow-lg border border-slate-200"
    >
      <h3 className="text-xl font-semibold text-slate-900 mb-6">Résultats</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-50">
          <div className="p-3 rounded-full bg-green-500/10">
            <Zap className="h-6 w-6 text-green-500" />
          </div>
          <div>
            <p className="text-sm text-slate-600">Consommation Énergétique</p>
            <p className="text-xl font-semibold text-slate-900">
              {results.totalEnergy_kWh.toLocaleString()} kWh
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-50">
          <div className="p-3 rounded-full bg-red-500/10">
            <Calculator className="h-6 w-6 text-red-500" />
          </div>
          <div>
            <p className="text-sm text-slate-600">Émissions de CO₂</p>
            <p className="text-xl font-semibold text-slate-900">
              {(results.totalCO2_kg/1000).toFixed(2)} tonnes CO₂e
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-50">
          <div className="p-3 rounded-full bg-blue-500/10">
            <Plane className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <p className="text-sm text-slate-600">Équivalent Vols Paris-NY</p>
            <p className="text-xl font-semibold text-slate-900">
              {results.flightsParisToNY.toFixed(1)} vols
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-50">
          <div className="p-3 rounded-full bg-emerald-500/10">
            <Leaf className="h-6 w-6 text-emerald-500" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm text-slate-600">Arbres à Planter pour compenser après</p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCompensationPeriod(5)}
                  className={`px-2 py-1 text-xs rounded ${
                    compensationPeriod === 5
                      ? 'bg-emerald-500 text-white'
                      : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                  }`}
                >
                  5 ans
                </button>
                <button
                  onClick={() => setCompensationPeriod(10)}
                  className={`px-2 py-1 text-xs rounded ${
                    compensationPeriod === 10
                      ? 'bg-emerald-500 text-white'
                      : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                  }`}
                >
                  10 ans
                </button>
                <button
                  onClick={() => setCompensationPeriod(20)}
                  className={`px-2 py-1 text-xs rounded ${
                    compensationPeriod === 20
                      ? 'bg-emerald-500 text-white'
                      : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                  }`}
                >
                  20 ans
                </button>
              </div>
            </div>
            <p className="text-xl font-semibold text-slate-900">
              {Math.ceil(treesToOffset)} arbres
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CarbonCalculatorResults; 